import { all, call, delay, put, race, select, take, takeLatest } from 'redux-saga/effects';
import {
  fetchCourseDetail,
  fetchUserTasks,
  fetchCallHistory,
  submitAssignment,
  rescheduleCall,
  submitIntake,
} from '../api/coachingApi.js';
import { activateCourse } from '../../courses/api/coursesApi.js';
import {
  coachingPageRequested,
  coachingPageLoaded,
  coachingPageFailed,
  coachingCleared,
  assignmentSubmitting,
  assignmentSubmitted,
  assignmentSubmitFailed,
  rescheduleRequested,
  rescheduleSubmitted,
  rescheduleFailed,
  intakeSubmitting,
  intakeSubmitted,
  intakeFailed,
  courseDetailRefreshed,
  callHistoryRefreshed,
} from './coachingSlice.js';
import { toastShown } from '../../system/store/toastSlice.js';
import { captureSagaError } from '../../../lib/sentry.js';
import { friendlyErrorMessage } from '../../../lib/errorCopy.js';
import { stripHtml } from '../../../lib/textUtils.js';

const ok = (r) => r && r.status && r.code === 200;
const BACKEND_SYNC_REFRESH_DELAY_MS = 3000;

function cleanBackendMessage(message) {
  return stripHtml(message).replace(/\s+/g, ' ').trim();
}

function supportEmailFromMessage(rawMessage, plainMessage) {
  const mailtoMatch = String(rawMessage || '').match(/mailto:([^"'?>\s]+)/i);
  const textMatch = String(plainMessage || '').match(/[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/i);
  const candidate = (mailtoMatch?.[1] || textMatch?.[0] || '').replace(/&amp;.*$/i, '');
  return /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(candidate) ? candidate : null;
}

export function coachingPageFailure(source, fallback = 'Failed to load coaching') {
  const response = source?.response;
  const body = response?.data ?? source;
  const rawMessage = typeof body?.message === 'string' && !(body instanceof Error)
    ? body.message
    : '';
  const plainMessage = cleanBackendMessage(rawMessage);
  const status = response?.status ?? body?.code;
  const expired = Number(status) === 404 && /course has expired/i.test(plainMessage);

  if (expired) {
    return {
      kind: 'expired',
      message: plainMessage || 'Your coaching course has expired.',
      supportEmail: supportEmailFromMessage(rawMessage, plainMessage),
    };
  }

  return {
    kind: 'generic',
    message: plainMessage || friendlyErrorMessage(source, fallback),
    supportEmail: null,
  };
}

function* failPage(failure) {
  yield put(coachingPageFailed(failure));
  if (failure.kind === 'expired') return;
  yield put(toastShown({
    variant: 'error',
    message: "We couldn't load your coaching page. Please try again.",
  }));
}

export function* handleCoachingPage(action) {
  try {
    const { courseId, waitForBooking = false, waitForAssignment = false } = action.payload;
    if (waitForBooking || waitForAssignment) {
      // Calendly and assignment uploads can confirm before the database reflects
      // the new coaching state. Wait once before fetching; leaving the page
      // cancels the pending refresh.
      const { cleared } = yield race({
        elapsed: delay(BACKEND_SYNC_REFRESH_DELAY_MS),
        cleared: take(coachingCleared.type),
      });
      if (cleared) return;
    }
    // POST /courses updates the active course in the database. Finish it before
    // fetching coaching data or allowing bookings/intake for this course.
    const activationRes = yield call(activateCourse, courseId);
    if (!ok(activationRes)) {
      yield* failPage(coachingPageFailure(activationRes, 'Failed to activate course'));
      return;
    }
    const [courseRes, tasksRes, historyRes] = yield all([
      call(fetchCourseDetail, courseId),
      call(fetchUserTasks),
      call(fetchCallHistory),
    ]);
    if (!ok(courseRes)) {
      yield* failPage(coachingPageFailure(courseRes, 'Failed to load course'));
      return;
    }
    if (!ok(tasksRes) || !ok(historyRes)) {
      yield* failPage(coachingPageFailure(null, 'Failed to load coaching data'));
      return;
    }
    yield put(coachingPageLoaded({
      courseDetail: courseRes.data,
      tasks: tasksRes.data ?? [],
      callHistory: historyRes.data ?? [],
    }));
  } catch (err) {
    const failure = coachingPageFailure(err);
    if (failure.kind !== 'expired') {
      captureSagaError(err, { action: 'coaching/coachingPageRequested' });
    }
    yield* failPage(failure);
  }
}

export function* handleAssignmentSubmit(action) {
  try {
    const { scheduleId, file } = action.payload;
    const res = yield call(submitAssignment, { file, scheduleId });
    if (!ok(res)) {
      yield put(assignmentSubmitFailed(res?.message || 'Submit failed'));
      return;
    }
    yield put(assignmentSubmitted());
    yield put(toastShown({
      variant: 'success',
      message: 'Assignment submitted. Please schedule your next call at least 48 hours from now so your coach can review it.',
    }));
    const courseId = yield select((s) => s.coaching.courseId);
    if (courseId != null) {
      yield put(coachingPageRequested({ courseId, waitForAssignment: true }));
    }
  } catch (err) {
    captureSagaError(err, { action: 'coaching/assignmentSubmitting' });
    yield put(assignmentSubmitFailed(err?.message || 'Submit failed'));
  }
}

export function* handleReschedule(action) {
  try {
    const { scheduleId, reasonType, reason } = action.payload;
    const res = yield call(rescheduleCall, { scheduleId, reasonType, reason });
    if (!ok(res)) {
      yield put(rescheduleFailed(res?.message || 'Reschedule failed'));
      return;
    }
    yield put(rescheduleSubmitted());
    const courseId = yield select((s) => s.coaching.courseId);
    if (courseId != null) {
      const [detail, history] = yield all([
        call(fetchCourseDetail, courseId),
        call(fetchCallHistory),
      ]);
      if (ok(detail))  yield put(courseDetailRefreshed(detail.data));
      if (ok(history)) yield put(callHistoryRefreshed(history.data ?? []));
    }
  } catch (err) {
    captureSagaError(err, { action: 'coaching/rescheduleRequested' });
    yield put(rescheduleFailed(err?.message || 'Reschedule failed'));
  }
}

export function* handleIntakeSubmit(action) {
  try {
    const res = yield call(submitIntake, action.payload);
    if (!ok(res)) {
      yield put(intakeFailed(res?.message || 'Intake submit failed'));
      return;
    }
    yield put(intakeSubmitted());
    const courseId = yield select((s) => s.coaching.courseId);
    if (courseId != null) {
      const detail = yield call(fetchCourseDetail, courseId);
      if (ok(detail)) {
        yield put(courseDetailRefreshed(detail.data));
      }
    }
  } catch (err) {
    captureSagaError(err, { action: 'coaching/intakeSubmitting' });
    yield put(intakeFailed(err?.message || 'Intake submit failed'));
  }
}

export default function* coachingSaga() {
  yield takeLatest(coachingPageRequested.type, handleCoachingPage);
  yield takeLatest(assignmentSubmitting.type, handleAssignmentSubmit);
  yield takeLatest(rescheduleRequested.type, handleReschedule);
  yield takeLatest(intakeSubmitting.type, handleIntakeSubmit);
}
